import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Users, Plus, Trash2, Loader2, Eye, EyeOff, UserCheck, UserX
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { DashboardLayout } from "./Dashboard";
import apiClient from "../../lib/api";
import { toastApiError } from "../../lib/toastApiError";

const SellersPage = () => {
  const { t, i18n } = useTranslation("pharmacy");
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newSeller, setNewSeller] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const dateLocale = i18n.language === "en" ? "en-US" : "fr-FR";

  useEffect(() => {
    fetchSellers();
  }, []);

  const fetchSellers = async () => {
    try {
      const res = await apiClient.get("/pharmacy/sellers");
      setSellers(res.data);
    } catch (error) {
      console.error("Error fetching sellers:", error);
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeller = async (e) => {
    e.preventDefault();

    if (!newSeller.name || !newSeller.email || !newSeller.password) {
      toast.error(t("sellers.toastFillAll"));
      return;
    }

    if (newSeller.password.length < 6) {
      toast.error(t("sellers.toastPasswordMin"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiClient.post("/pharmacy/sellers", newSeller);
      setSellers([...sellers, res.data]);
      setShowAddDialog(false);
      setNewSeller({ name: "", email: "", password: "" });
      toast.success(t("sellers.toastCreated"));
    } catch (error) {
      toastApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (sellerId, currentStatus) => {
    try {
      await apiClient.put(`/pharmacy/sellers/${sellerId}`, { active: !currentStatus });
      setSellers(sellers.map(s => s.id === sellerId ? { ...s, active: !currentStatus } : s));
      toast.success(currentStatus ? t("sellers.toastDeactivated") : t("sellers.toastActivated"));
    } catch (error) {
      toastApiError(error);
    }
  };

  const handleDeleteSeller = async (sellerId) => {
    if (!window.confirm(t("sellers.toastConfirmDelete"))) return;

    try {
      await apiClient.delete(`/pharmacy/sellers/${sellerId}`);
      setSellers(sellers.filter(s => s.id !== sellerId));
      toast.success(t("sellers.toastDeleted"));
    } catch (error) {
      toastApiError(error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(dateLocale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <DashboardLayout title={t("sellers.title")}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-12 w-12 animate-spin text-omu-blue-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t("sellers.title")}>
      <Alert className="mb-6 bg-omu-blue-50 border-omu-blue-200">
        <Users className="h-4 w-4 text-omu-blue-600" />
        <AlertDescription className="text-omu-blue-800">
          {t("sellers.infoAlert")}
        </AlertDescription>
      </Alert>

      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-slate-500">{t("sellers.count", { count: sellers.length })}</p>
        </div>
        <Button
          className="bg-omu-blue-500 hover:bg-omu-blue-600 rounded-full"
          onClick={() => setShowAddDialog(true)}
          data-testid="add-seller-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("sellers.addSeller")}
        </Button>
      </div>

      <Card className="border-0 shadow-soft">
        <CardContent className="p-0">
          {sellers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{t("sellers.empty")}</p>
              <p className="text-sm text-slate-400 mt-2">{t("sellers.emptyHint")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("sellers.colName")}</TableHead>
                    <TableHead>{t("sellers.colEmail")}</TableHead>
                    <TableHead>{t("sellers.colStatus")}</TableHead>
                    <TableHead>{t("sellers.colCreated")}</TableHead>
                    <TableHead className="text-right">{t("sellers.colActions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sellers.map((seller) => (
                    <TableRow key={seller.id}>
                      <TableCell className="font-medium">{seller.name}</TableCell>
                      <TableCell className="text-slate-500">{seller.email}</TableCell>
                      <TableCell>
                        <Badge className={seller.active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}>
                          {seller.active ? t("status.active") : t("status.inactive")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500">{formatDate(seller.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(seller.id, seller.active)}
                            title={seller.active ? t("actions.deactivate") : t("actions.activate")}
                          >
                            {seller.active ? (
                              <UserX className="h-4 w-4 text-amber-500" />
                            ) : (
                              <UserCheck className="h-4 w-4 text-green-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteSeller(seller.id)}
                            title={t("actions.delete")}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("sellers.addDialogTitle")}</DialogTitle>
            <DialogDescription>{t("sellers.addDialogDesc")}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleAddSeller} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="seller-name">{t("sellers.fullName")}</Label>
              <Input
                id="seller-name"
                placeholder={t("sellers.fullNamePlaceholder")}
                value={newSeller.name}
                onChange={(e) => setNewSeller({ ...newSeller, name: e.target.value })}
                data-testid="seller-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller-email">{t("sellers.email")}</Label>
              <Input
                id="seller-email"
                type="email"
                placeholder="vendeur@email.com"
                value={newSeller.email}
                onChange={(e) => setNewSeller({ ...newSeller, email: e.target.value })}
                data-testid="seller-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="seller-password">{t("sellers.password")}</Label>
              <div className="relative">
                <Input
                  id="seller-password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("sellers.passwordPlaceholder")}
                  value={newSeller.password}
                  onChange={(e) => setNewSeller({ ...newSeller, password: e.target.value })}
                  className="pr-10"
                  data-testid="seller-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                className="bg-omu-blue-500 hover:bg-omu-blue-600"
                disabled={submitting}
                data-testid="create-seller-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {t("sellers.createSeller")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default SellersPage;
